import type {SiteLocale} from "seo/siteMeta";
import {formatInUnit} from "core/format/byteFormat";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";

/**
 * Per-page copy for the public **Advertising** landing page (`/advertising`).
 *
 * Every locale lives here and the page selects one with `useLocale()`, following the ui#133
 * explainer-page shape, so the `/ro` and `/de` SSG trees render their own language for free. Since ui#1861 this page has no
 * second surface: the Resources menu NAVIGATES here rather than opening a body in `ResourceDialog`, and
 * the prose body that dialog used to render is gone.
 *
 * ## Accuracy contract
 *
 * This page SELLS the takeover, so every claim below is grounded in what the booking flow and the
 * recipient-facing takeover actually do — not in what an ad page would like them to do. The bands, in
 * order, and where each one's content comes from:
 *
 *  1. HERO. The headline, its drawn accent and the eyebrow are the ui#1156 originals. Beside them is a
 *     browser-frame MOCK of a download page: a token-only stand-in card and ad chip, with the REAL
 *     {@link ../../sponsor/takeover/SponsorTakeover.SponsorTakeoverArt} running behind it on the
 *     token-derived plates from `placeholderArtUrls`. The renderer is genuinely the recipient's — that
 *     is what keeps the demo honest — and {@link AdvertisingHeroCopy.demoCaption} says so, because a
 *     moving backdrop with no caption reads as a stock background or as somebody's real campaign.
 *  2. TERMS. Three ruled cells, and NOT ONE of their figures is written here. "1 advertiser per day" is
 *     the rule availability enforces (one booking per day); the treatment count is
 *     `ADVERTISED_TREATMENTS.length` (kitecorp/transferify-ui#1870 — it was `SPONSOR_TEMPLATES.length`
 *     while the catalogue was backdrops only, and that list is now half of it), never a literal,
 *     because a restated count has been wrong once already (ui#624); the run length is
 *     `availability.maxDays`, read live and OMITTED when the read fails.
 *  3. STEPS. Four, mirroring the booking page's own sequence (`sponsor.steps`). The titles are held here
 *     rather than read from that catalog because the fourth is not one of its headings: the form's last
 *     step is "Review and pay", and this band names the outcome ("Pay once") rather than the screen.
 *  4. TREATMENTS. One card per `ADVERTISED_TREATMENTS` entry, in the two labelled category rows
 *     `categoryGroups` builds — backdrops, then wallpapers (kitecorp/transferify-ui#1870). Named and
 *     described from `sponsor.templates[key]`, headed by `sponsor.categories[category].name` and
 *     counted by `templateAssetRange` — the booking page's own catalog and the booking page's own
 *     category headings, so a reworded treatment moves both surfaces at once. Nothing about them is
 *     restated here, INCLUDING the count in the band's own heading, which takes the same figure the
 *     terms strip does. `ADVERTISED_TREATMENTS` rather than the raw catalogue, and that module states
 *     its two conditions in full: every card is a live preview linking to the booking form, so a
 *     treatment this bundle cannot animate or the form does not offer would be a card that shows
 *     nothing and sells nothing.
 *  5. TWO LISTS. Who is on the other side, and what we promise — the claims the ui#1128 prose already
 *     made, cut to a line each. No new claim: a recipient needs no account (they clicked a link), we
 *     profile nobody and sell no segments, a person reviews every booking, a booking we cannot run is
 *     refunded in full (`sponsor.summary.reviewNote`), and the ad is ALWAYS labelled as an ad
 *     (`sponsorTakeover.label` — a legal obligation from the 2026-08-06 review, never conditional).
 *  6. CLOSER. The ui#1156 self-serve promise, plus a fourteen-day availability row computed by
 *     {@link ../advertisingAvailability.ts} from the same live read, with a pending day counted as taken
 *     exactly as the booking calendar counts it. The row is absent when the read failed.
 *
 * ## What is deliberately NOT here
 *
 * NO PRICE, in any band. The daily rate is an anonymous read (`SponsorApi.availability`), and this page
 * does not display it (owner, 2026-09-02): the rate belongs to the booking page, next to the calendar
 * that qualifies it, and a figure quoted a page earlier is a figure that goes stale in a screenshot, a
 * cache or a translation. That is why the hero's secondary link SENDS a reader to the calendar for it
 * rather than answering it here. Nothing on this page reads `dailyRateMinor` or `currency`.
 *
 * No measured figure either — no "100% viewable", no impression count. We publish no viewability metric,
 * and a number nobody measures is the kind of claim this contract exists to keep off the page. The ONE
 * hand-written number is the closer's "five minutes", the owner's own approved wording (2026-08-17,
 * ui#1156) for how long the self-serve flow takes rather than a measurement of anything; it is recorded
 * here so the exception stays visible instead of looking like an oversight.
 *
 * Two older sentences did not survive the ui#1861 rewrite, and their absence is the point. The prose
 * `intro[0]` claimed a "full-width" takeover, which the page contradicts: at or above the 1024px
 * showcase the art stops before the card column (`download/recipient/primitives.tsx`), so it is a region
 * rather than the whole screen. And nothing here says "nothing else on the screen selling anything" — an
 * unbranded download page still carries its "Powered by Transferify" footer, which the hero's own mock
 * now shows rather than hides.
 *
 * The claims that DID survive are each still anchored: "no second advertiser, no rotation, no share of
 * voice to negotiate" holds because availability keeps one booking per day and `decidePageBackdrop`
 * serves at most one; "no contact form, no proposal, no waiting for somebody to call you back" is an
 * absence we can point at, because `SponsorPage` prices, schedules, collects the creative and checks
 * out with no human in the path before payment; and "pricing a run needs no account; booking one needs a
 * login" restates the shipped rule rather than softening it, because "no account needed" alone would be
 * a promise the checkout then breaks.
 */

/**
 * The landing hero: the display headline, its drawn accent, the pitch and the download-page mock beside
 * them (ui#1156, reworked in ui#1861).
 *
 * The headline is held as a WHOLE SENTENCE plus the word to mark, never as three fragments — see
 * {@link ../accentedHeadline.ts}, which owns the split. Each locale therefore writes its own complete
 * sentence and names its own accent word, and neither is constrained by the other's word order.
 */
export interface AdvertisingHeroCopy {
	/** The `<h1>` — one complete sentence. */
	headline: string;
	/**
	 * The word inside {@link headline} that gets the pencil stroke.
	 *
	 * It must appear in the headline as a standalone word. If a reword ever drops it, the headline still
	 * renders in full, without a stroke — the fallback is stated and tested rather than left to chance.
	 */
	accentWord: string;
	/** The pitch, directly under the display type. */
	lede: string;
	/**
	 * The text link beside the primary button.
	 *
	 * It promises the RATE, which this page deliberately does not show (see the accuracy contract). That
	 * is the link's job: the number lives one click away, beside the calendar that qualifies it.
	 */
	secondary: string;
	/** The download-page mock beside the pitch. */
	mock: AdvertisingMockCopy;
	/**
	 * Caption under the mock.
	 *
	 * Load-bearing rather than decorative: the backdrop inside the frame is the REAL renderer fed
	 * token-derived stand-in plates, and a moving demo with no caption reads as a stock background or —
	 * worse — as somebody's actual campaign. It says which treatment is on screen, how many there are,
	 * and that the art is a stand-in.
	 *
	 * Takes the treatment's name rather than spelling it out, because the hero picks which treatment to
	 * run and this sentence has to agree with that choice. Written out here, changing the constant would
	 * leave both locales quietly naming the wrong one — and it would be a third copy of a string the
	 * booking page's own catalog already owns (`sponsor.templates.<KEY>.name`).
	 *
	 * The COUNT is passed in for the same reason (kitecorp/transferify-ui#1870): both locales said "one
	 * of six treatments" in prose, which the wallpaper catalogue made wrong the day it shipped.
	 */
	demoCaption: (treatment: string, treatments: number) => string;
}

/**
 * The browser-frame mock: a picture of the surface being sold, drawn from tokens rather than screenshot.
 *
 * Every string is a STAND-IN, and each is written to look like one. The sender's name, the file names
 * and the sizes are invented; the ad chip says "Your headline here" rather than pretending to be a
 * brand. The frame is announced to assistive technology once, through {@link frameAria}, and its
 * interior is hidden from it — nothing in there is real, and a screen reader that read out two file
 * names and a "Download all" button would be describing a transfer that does not exist.
 */
export interface AdvertisingMockCopy {
	/** The frame's accessible name — what this picture IS, said once. */
	frameAria: string;
	/** The address bar, built from the locale's own site host so the mock names the right brand. */
	url: (host: string) => string;
	/** Above the file list, as the real download card writes it. */
	sender: string;
	/** The card's heading. */
	fileCount: string;
	/** The rows: a type badge, a name and a size. Invented, and written to read as invented. */
	files: readonly {kind: string; name: string; size: string}[];
	/** The card's primary action. */
	download: string;
	/** The ad chip's stand-in copy. Its "Ad" label is the real one, from `sponsorTakeover.label`. */
	ad: {
		headline: string;
		/** Interpolated into the real `sponsorTakeover.byLine`, so the chip reads as it really reads. */
		sponsor: string;
		tagline: string;
		cta: string;
	};
}

/** One cell of the terms strip: a figure, what the figure counts, and one line of why it matters. */
export interface AdvertisingTermCopy {
	/** What the figure counts, pluralised by the figure itself. */
	unit: (count: number) => string;
	/** The line under it. */
	caption: string;
}

/** The terms strip. Its figures are live or derived; only these words are written here. */
export interface AdvertisingTermsCopy {
	/** The section's accessible name — the strip has figures, not a heading. */
	aria: string;
	/** Marks a cell whose figure came from the live availability read rather than from the product. */
	liveLabel: string;
	advertisers: AdvertisingTermCopy;
	treatments: AdvertisingTermCopy;
	runLength: AdvertisingTermCopy;
}

/** One of the four booking steps. */
export interface AdvertisingStepCopy {
	title: string;
	line: string;
}

export interface AdvertisingStepsCopy {
	heading: string;
	lede: string;
	/** "Step 1" — numbered because these ARE the booking page's sequence, in its order. */
	stepLabel: (position: number) => string;
	steps: readonly AdvertisingStepCopy[];
}

/**
 * The treatment gallery. Every name, description and image count comes from `sponsor.templates`, and
 * every category heading from `sponsor.categories` — the booking form's own two groups.
 */
export interface AdvertisingTreatmentsCopy {
	/**
	 * The band's `<h2>`, as a function of how many treatments there are.
	 *
	 * A function since kitecorp/transferify-ui#1870, when the catalogue stopped being six. It read
	 * "Six ways to hold the frame." and was the last written-out count on a page whose whole accuracy
	 * contract is that it restates nothing.
	 */
	heading: (treatments: number) => string;
	lede: string;
}

/** One line of a short list: a bolded lead and the rest of the sentence. */
export interface AdvertisingPointCopy {
	lead: string;
	rest: string;
}

export interface AdvertisingListCopy {
	heading: string;
	points: readonly AdvertisingPointCopy[];
}

/** The fourteen-day row in the closing band, drawn from the same availability read. */
export interface AdvertisingAvailabilityCopy {
	/** The row's accessible name. */
	aria: (days: number) => string;
	/** Its heading. */
	title: (days: number) => string;
	/** Marks it as a live read rather than a published schedule. */
	liveLabel: string;
	/** Precedes the answer, its own punctuation included — the view joins the two with a space only. */
	nextFreeLabel: string;
	/** The answer when today itself is still bookable. */
	today: string;
	/** The answer when tomorrow is the first free day. */
	tomorrow: string;
	/**
	 * The answer otherwise, from the parts `sponsor.calendar` already ships in both locales — never
	 * `toLocaleDateString`, which would put English weekday and month names on the Romanian page.
	 */
	onDate: (weekday: string, dayOfMonth: number, month: string) => string;
	/** Every day in the row is spoken for. A real state, and the row still renders to show it. */
	noneFree: (days: number) => string;
}

/** The closing section: the self-serve difference, stated plainly, beside the availability row. */
export interface AdvertisingCloserCopy {
	/** `<h2>` — the owner's "Book a day in five minutes. No sales calls." */
	heading: string;
	/** One paragraph. The ui#1128 second paragraph pointed at a rate this page no longer shows. */
	paragraph: string;
	availability: AdvertisingAvailabilityCopy;
}

/** Full copy for the advertising landing page. */
export interface AdvertisingCopy {
	/** `<Seo>` metadata — the browser/search title and meta description for this page. */
	seo: {
		/** Page title (the site name is appended automatically by `<Seo>`). */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/**
	 * The page's name for itself: the hero's eyebrow.
	 *
	 * It stopped being the `<h1>` at ui#1156, when the hero took that role, and stopped being the
	 * Resources dialog's accessible name at ui#1861, when the menu entry became plain navigation. It
	 * still names the SUBJECT above a headline that makes a promise.
	 */
	heading: string;
	hero: AdvertisingHeroCopy;
	terms: AdvertisingTermsCopy;
	steps: AdvertisingStepsCopy;
	treatments: AdvertisingTreatmentsCopy;
	/** "Who is on the other side" — who a recipient is, and who they are not. */
	audience: AdvertisingListCopy;
	/** "What we promise" — review, refund, and the mandatory label. */
	promises: AdvertisingListCopy;
	closer: AdvertisingCloserCopy;
	/** The hand-off to the booking flow, offered in the hero and again in the closer. */
	cta: {
		/** Button label — the owner's "Start advertising". */
		label: string;
		/** Sits beside the button in the closing band: what the next page does before it asks for anything. */
		note: string;
	};
}

/** EN + RO + DE + FR + IT copy, keyed by locale. The page reads `advertisingCopy[locale]`. */
export const advertisingCopy: Record<SiteLocale, AdvertisingCopy> = localeCopyWithEnglishFallback<AdvertisingCopy>({
	en: {
		seo: {
			title: "Advertise with us - sponsored downloads",
			description:
				"Put your brand on the page recipients wait on while their files arrive. One advertiser " +
				"at a time, booked by the day, reviewed by a person before it runs.",
		},
		heading: "Advertising on Transferify",
		hero: {
			headline: "Own the moment they wait for.",
			accentWord: "wait",
			lede:
				"Every file we deliver ends on a download page somebody is actively watching. Book that " +
				"page by the day. No second advertiser, no rotation, no share of voice to negotiate.",
			secondary: "See the calendar and today's rate",
			mock: {
				frameAria:
					"The download page a recipient sees, with a takeover running behind the download card.",
				url: host => `${host}/download/…`,
				sender: "Ana sent you",
				fileCount: "2 files",
				files: [
					{kind: "MP4", name: "Brand_reel_final.mp4", size: "1.2 GB"},
					{kind: "PDF", name: "Lookbook_2026.pdf", size: "48 MB"},
				],
				download: "Download all",
				ad: {
					headline: "Your headline here",
					sponsor: "Your brand",
					tagline: "An optional second line.",
					cta: "Learn more",
				},
			},
			demoCaption: (treatment, treatments) =>
				`A live example: ${treatment}, one of ${treatments} treatments, running the real ` +
				"renderer with stand-in art.",
		},
		terms: {
			aria: "What a booking gets you",
			liveLabel: "Live",
			advertisers: {
				unit: () => "advertiser per day",
				caption: "A day is yours or somebody else's. No rotation, no share of voice.",
			},
			treatments: {
				unit: count => (count === 1 ? "treatment" : "treatments"),
				// It stopped being true of IMAGES alone at kitecorp/transferify-ui#1870: a wallpaper takes
				// the page rather than the strip behind the card, and one of them takes a video and no
				// images at all.
				caption: "Each animates differently, behind the download card or over the whole page.",
			},
			runLength: {
				unit: count => (count === 1 ? "day per run" : "days per run"),
				caption: "One day, or a run of consecutive days, paid once.",
			},
		},
		steps: {
			heading: "Four steps, then it runs.",
			lede:
				"The same four steps the booking page walks you through. Pricing a run needs no account; " +
				"booking one needs a login, so your bookings have somewhere to live.",
			stepLabel: position => `Step ${position}`,
			steps: [
				{
					title: "Choose a look",
					// Names the two CATEGORIES rather than counting the treatments: the count is derived
					// everywhere else on this page, and a step line is the one place it would have to be
					// written out.
					line: "Backdrops and wallpapers, each with a live preview while you pick.",
				},
				{
					title: "Add your art",
					line: "PNG, JPEG or WebP, plus a headline, a button and the link it opens.",
				},
				{
					title: "Pick your dates",
					line: "One day or a run of consecutive days. A day is free or it is not.",
				},
				{
					title: "Pay once",
					line: "The total is per day, tax at checkout. A person reviews it before it runs.",
				},
			],
		},
		treatments: {
			heading: treatments => (treatments === 1
				? "One way to hold the frame."
				: `${treatments} ways to hold the frame.`),
			lede:
				"A backdrop animates behind the download card, so the page still does its job while your " +
				"brand fills the frame. A wallpaper takes the whole page and your words are typeset on " +
				"it. Every preview below is the real renderer.",
		},
		audience: {
			heading: "Who is on the other side",
			points: [
				{
					lead: "People waiting for their files.",
					rest:
						"A recipient needs no account, no plan and no app. They clicked a link someone they " +
						"know sent them.",
				},
				{
					lead: "Attention that is not divided.",
					rest: "Nobody scrolls past a download they are waiting for.",
				},
				{
					lead: "Broad, not targeted.",
					rest:
						"We do not profile recipients or sell segments. You buy a day on a page, not a slice " +
						"of a crowd.",
				},
			],
		},
		promises: {
			heading: "What we promise",
			points: [
				{lead: "A person reviews every booking", rest: "before it reaches a recipient."},
				{
					lead: "A full refund if we cannot run it.",
					rest:
						"We would rather return your money than put something in front of a stranger who did " +
						"not ask for it.",
				},
				{
					lead: "Always labelled as an ad,",
					rest:
						"on every treatment and at every screen size. A legal obligation, never a layout " +
						"choice.",
				},
			],
		},
		closer: {
			heading: "Book a day in five minutes. No sales calls.",
			paragraph:
				"No contact form, no proposal, no waiting for somebody to call you back. Pick a treatment, " +
				"upload your art, choose your days and pay. That is the whole of it.",
			availability: {
				aria: days => `Availability for the next ${days} days`,
				title: days => `Next ${days} days`,
				liveLabel: "Live availability",
				nextFreeLabel: "Next free day:",
				today: "today",
				tomorrow: "tomorrow",
				onDate: (weekday, dayOfMonth, month) => `${weekday} ${dayOfMonth} ${month}`,
				noneFree: days => `Nothing free in the next ${days} days`,
			},
		},
		cta: {
			label: "Start advertising",
			note: "No account needed to look.",
		},
	},
	ro: {
		seo: {
			title: "Publicitate pe pagina de descărcare",
			description:
				"Pune-ți brandul pe pagina pe care destinatarii o așteaptă cât timp le vin fișierele. Un " +
				"singur advertiser o dată, rezervare pe zile.",
		},
		heading: "Publicitate pe Transferify",
		hero: {
			headline: "Fii văzut exact când toți așteaptă.",
			accentWord: "așteaptă",
			lede:
				"Fiecare fișier pe care îl livrăm se termină pe o pagină de descărcare pe care cineva chiar " +
				"o urmărește. Rezervi pagina aceea pe zile. Niciun al doilea advertiser, nicio rotație și " +
				"niciun share of voice de negociat.",
			secondary: "Vezi calendarul și tariful de azi",
			mock: {
				frameAria:
					"Pagina de descărcare așa cum o vede un destinatar, cu un takeover care rulează în " +
					"spatele cardului de descărcare.",
				url: host => `${host}/download/…`,
				sender: "Ana ți-a trimis",
				fileCount: "2 fișiere",
				files: [
					{kind: "MP4", name: "Brand_reel_final.mp4", size: "1,2 GB"},
					{kind: "PDF", name: "Lookbook_2026.pdf", size: "48 MB"},
				],
				download: "Descarcă tot",
				ad: {
					headline: "Titlul tău aici",
					sponsor: "Brandul tău",
					tagline: "Un al doilea rând, opțional.",
					cta: "Află mai multe",
				},
			},
			demoCaption: (treatment, treatments) =>
				`Un exemplu live: ${treatment}, unul dintre cele ${treatments} tratamente, rulat de ` +
				"motorul real cu imagini de test.",
		},
		terms: {
			aria: "Ce primești cu o rezervare",
			liveLabel: "Live",
			advertisers: {
				unit: () => "advertiser pe zi",
				caption: "O zi este a ta sau a altcuiva. Fără rotație, fără share of voice.",
			},
			treatments: {
				unit: count => (count === 1 ? "tratament" : "tratamente"),
				caption: "Fiecare se animă diferit, în spatele cardului de descărcare sau peste toată pagina.",
			},
			runLength: {
				unit: count => (count === 1 ? "zi pe rezervare" : "zile pe rezervare"),
				caption: "O singură zi sau mai multe zile consecutive, plătite o dată.",
			},
		},
		steps: {
			heading: "Patru pași și pornește.",
			lede:
				"Aceiași patru pași pe care îi parcurgi pe pagina de rezervare. Ca să afli prețul nu ai " +
				"nevoie de cont; ca să rezervi ai nevoie de autentificare, ca rezervările tale să aibă unde " +
				"să stea.",
			stepLabel: position => `Pasul ${position}`,
			steps: [
				{
					title: "Alege un stil",
					line: "Fundaluri și tapete, fiecare cu o previzualizare live cât timp alegi.",
				},
				{
					title: "Adaugă-ți imaginile",
					line: "PNG, JPEG sau WebP, plus un titlu, un buton și linkul pe care îl deschide.",
				},
				{
					title: "Alege-ți zilele",
					line: "O singură zi sau mai multe zile consecutive. O zi este liberă sau nu este.",
				},
				{
					title: "Plătești o dată",
					line: "Totalul este pe zi, taxele la checkout. Un om o verifică înainte să ruleze.",
				},
			],
		},
		treatments: {
			heading: treatments => (treatments === 1
				? "Un singur fel de a ține cadrul."
				: `${treatments} feluri de a ține cadrul.`),
			lede:
				"Un fundal se animă în spatele cardului de descărcare, așa că pagina își face în " +
				"continuare treaba în timp ce brandul tău umple cadrul. Un tapet ia toată pagina, iar " +
				"cuvintele tale sunt așezate peste ea. Fiecare previzualizare de mai jos este motorul real.",
		},
		audience: {
			heading: "Cine este de cealaltă parte",
			points: [
				{
					lead: "Oameni care își așteaptă fișierele.",
					rest:
						"Un destinatar nu are nevoie de cont, de abonament sau de aplicație. A dat clic pe un " +
						"link primit de la cineva cunoscut.",
				},
				{
					lead: "Atenție neîmpărțită.",
					rest: "Nimeni nu derulează peste o descărcare pe care o așteaptă.",
				},
				{
					lead: "Larg, nu țintit.",
					rest:
						"Nu profilăm destinatarii și nu vindem segmente. Cumperi o zi pe o pagină, nu o felie " +
						"dintr-o mulțime.",
				},
			],
		},
		promises: {
			heading: "Ce promitem",
			points: [
				{lead: "Un om verifică fiecare rezervare", rest: "înainte să ajungă la un destinatar."},
				{
					lead: "Banii înapoi integral dacă nu o putem rula.",
					rest:
						"Preferăm să îți returnăm banii decât să punem ceva în fața unui străin care nu a " +
						"cerut asta.",
				},
				{
					lead: "Întotdeauna marcată ca publicitate,",
					rest:
						"la orice tratament și la orice dimensiune de ecran. O obligație legală, niciodată o " +
						"alegere de layout.",
				},
			],
		},
		closer: {
			heading: "Rezervi o zi în cinci minute. Fără apeluri de vânzări.",
			paragraph:
				"Fără formular de contact, fără ofertă și fără să aștepți să te sune cineva. Alegi un " +
				"tratament, îți încarci imaginile, îți alegi zilele și plătești. Asta e tot.",
			availability: {
				aria: days => `Disponibilitatea pentru următoarele ${days} zile`,
				title: days => `Următoarele ${days} zile`,
				liveLabel: "Disponibilitate live",
				nextFreeLabel: "Prima zi liberă:",
				today: "azi",
				tomorrow: "mâine",
				onDate: (weekday, dayOfMonth, month) => `${weekday}, ${dayOfMonth} ${month}`,
				noneFree: days => `Nicio zi liberă în următoarele ${days} zile`,
			},
		},
		cta: {
			label: "Începe să faci publicitate",
			note: "Nu ai nevoie de cont ca să te uiți.",
		},
	},
	de: {
		seo: {
			title: "Werbung auf der Download-Seite",
			description:
				"Bring deine Marke auf die Seite, auf der Empfänger warten, während ihre Dateien ankommen. " +
				"Ein Werbetreibender pro Tag, tageweise gebucht.",
		},
		heading: "Werbung auf Transferify",
		hero: {
			headline: "Dir gehört der Moment, auf den alle warten.",
			accentWord: "warten",
			lede:
				"Jede Datei, die wir ausliefern, endet auf einer Download-Seite, die jemand aktiv im Blick " +
				"hat. Buch diese Seite tageweise. Kein zweiter Werbetreibender, keine Rotation, kein Share " +
				"of Voice zu verhandeln.",
			secondary: "Kalender und heutigen Tarif ansehen",
			mock: {
				frameAria:
					"Die Download-Seite, wie ein Empfänger sie sieht, mit einem Takeover hinter der Download-Karte.",
				url: host => `${host}/download/…`,
				sender: "Ana hat dir etwas geschickt",
				fileCount: "2 Dateien",
				files: [
					{kind: "MP4", name: "Brand_reel_final.mp4", size: "1,2 GB"},
					{kind: "PDF", name: "Lookbook_2026.pdf", size: "48 MB"},
				],
				download: "Alle herunterladen",
				ad: {
					headline: "Deine Überschrift hier",
					sponsor: "Deine Marke",
					tagline: "Eine optionale zweite Zeile.",
					cta: "Mehr erfahren",
				},
			},
			demoCaption: (treatment, treatments) =>
				`Ein Live-Beispiel: ${treatment}, eine von ${treatments} Gestaltungen, gerendert vom echten ` +
				"Renderer mit Platzhalter-Grafik.",
		},
		terms: {
			aria: "Was du mit einer Buchung bekommst",
			liveLabel: "Live",
			advertisers: {
				unit: () => "Werbetreibender pro Tag",
				caption: "Ein Tag gehört dir oder jemand anderem. Keine Rotation, kein Share of Voice.",
			},
			treatments: {
				unit: count => (count === 1 ? "Gestaltung" : "Gestaltungen"),
				caption: "Jede animiert anders, hinter der Download-Karte oder über die ganze Seite.",
			},
			runLength: {
				unit: count => (count === 1 ? "Tag pro Buchung" : "Tage pro Buchung"),
				caption: "Ein Tag oder mehrere aufeinanderfolgende Tage, einmal bezahlt.",
			},
		},
		steps: {
			heading: "Vier Schritte, dann läuft es.",
			lede:
				"Dieselben vier Schritte, durch die dich die Buchungsseite führt. Für einen Preis brauchst " +
				"du kein Konto; zum Buchen brauchst du eine Anmeldung, damit deine Buchungen einen Ort haben.",
			stepLabel: position => `Schritt ${position}`,
			steps: [
				{
					title: "Wähl einen Look",
					line: "Backdrops und Hintergrundbilder, jeweils mit einer Live-Vorschau beim Aussuchen.",
				},
				{
					title: "Füg deine Grafik hinzu",
					line: "PNG, JPEG oder WebP, dazu eine Überschrift, ein Button und der Link, den er öffnet.",
				},
				{
					title: "Wähl deine Tage",
					line: "Ein Tag oder mehrere aufeinanderfolgende Tage. Ein Tag ist frei oder er ist es nicht.",
				},
				{
					title: "Einmal zahlen",
					line: "Der Preis gilt pro Tag, Steuern beim Checkout. Ein Mensch prüft es, bevor es läuft.",
				},
			],
		},
		treatments: {
			heading: treatments => (treatments === 1
				? "Eine Art, das Bild zu halten."
				: `${treatments} Arten, das Bild zu halten.`),
			lede:
				"Ein Backdrop animiert hinter der Download-Karte, sodass die Seite ihre Arbeit weiter tut, " +
				"während deine Marke das Bild füllt. Ein Hintergrundbild nimmt die ganze Seite, und deine " +
				"Worte werden darauf gesetzt. Jede Vorschau unten ist der echte Renderer.",
		},
		audience: {
			heading: "Wer auf der anderen Seite ist",
			points: [
				{
					lead: "Menschen, die auf ihre Dateien warten.",
					rest:
						"Ein Empfänger braucht kein Konto, keinen Plan und keine App. Er hat auf einen Link " +
						"geklickt, den ihm jemand Bekanntes geschickt hat.",
				},
				{
					lead: "Ungeteilte Aufmerksamkeit.",
					rest: "Niemand scrollt an einem Download vorbei, auf den er wartet.",
				},
				{
					lead: "Breit, nicht zielgerichtet.",
					rest:
						"Wir profilieren keine Empfänger und verkaufen keine Segmente. Du kaufst einen Tag auf " +
						"einer Seite, kein Stück einer Menge.",
				},
			],
		},
		promises: {
			heading: "Was wir versprechen",
			points: [
				{lead: "Ein Mensch prüft jede Buchung", rest: "bevor sie einen Empfänger erreicht."},
				{
					lead: "Volle Erstattung, wenn wir sie nicht ausspielen können.",
					rest:
						"Uns ist lieber, dir dein Geld zurückzugeben, als einem Fremden etwas vorzusetzen, der " +
						"nicht danach gefragt hat.",
				},
				{
					lead: "Immer als Werbung gekennzeichnet,",
					rest:
						"bei jeder Gestaltung und in jeder Bildschirmgröße. Eine gesetzliche Pflicht, nie eine " +
						"Layout-Entscheidung.",
				},
			],
		},
		closer: {
			heading: "Buch einen Tag in fünf Minuten. Keine Verkaufsgespräche.",
			paragraph:
				"Kein Kontaktformular, kein Angebot, kein Warten auf einen Rückruf. Wähl eine Gestaltung, " +
				"lad deine Grafik hoch, such dir die Tage aus und zahl. Das ist alles.",
			availability: {
				aria: days => `Verfügbarkeit für die nächsten ${days} Tage`,
				title: days => `Nächste ${days} Tage`,
				liveLabel: "Live-Verfügbarkeit",
				nextFreeLabel: "Nächster freier Tag:",
				today: "heute",
				tomorrow: "morgen",
				onDate: (weekday, dayOfMonth, month) => `${weekday}, ${dayOfMonth}. ${month}`,
				noneFree: days => `Nichts frei in den nächsten ${days} Tagen`,
			},
		},
		cta: {
			label: "Mit Werbung starten",
			note: "Zum Ansehen brauchst du kein Konto.",
		},
	},
	fr: {
		seo: {
			title: "Annoncer\u00a0: téléchargements sponsorisés",
			description:
				"Placez votre marque sur la page que les destinataires regardent pendant que leurs fichiers arrivent. " +
				"Un annonceur à la fois, réservé au jour.",
		},
		heading: "La publicité sur Transferify",
		hero: {
			headline: "Occupez le moment où ils attendent.",
			accentWord: "attendent",
			lede:
				"Chaque fichier que nous livrons se termine sur une page de téléchargement que quelqu’un regarde " +
				"activement. Réservez cette page au jour. Pas de second annonceur, pas de rotation, pas de part de " +
				"voix à négocier.",
			secondary: "Voir le calendrier et le tarif du jour",
			mock: {
				frameAria:
					"La page de téléchargement que voit un destinataire, avec un habillage publicitaire derrière la carte de téléchargement.",
				url: host => `${host}/download/…`,
				sender: "Ana vous a envoyé",
				fileCount: "2 fichiers",
				files: [
					{kind: "MP4", name: "Film_marque_final.mp4", size: formatInUnit(1.2, "GB", "fr", 1)},
					{kind: "PDF", name: "Catalogue_2026.pdf", size: formatInUnit(48, "MB", "fr", 0)},
				],
				download: "Tout télécharger",
				ad: {
					headline: "Votre titre ici",
					sponsor: "Votre marque",
					tagline: "Une deuxième ligne facultative.",
					cta: "En savoir plus",
				},
			},
			demoCaption: (treatment, treatments) =>
				`Un exemple en direct\u00a0: ${treatment}, l’une des ${treatments} apparences, avec le vrai moteur de rendu ` +
				"et des visuels de substitution.",
		},
		terms: {
			aria: "Ce que vous obtenez avec une réservation",
			liveLabel: "En direct",
			advertisers: {
				unit: () => "annonceur par jour",
				caption: "Un jour est à vous ou à quelqu’un d’autre. Pas de rotation, pas de part de voix.",
			},
			treatments: {
				unit: count => (count < 2 ? "apparence" : "apparences"),
				caption: "Chacune s’anime à sa façon, derrière la carte de téléchargement ou sur toute la page.",
			},
			runLength: {
				unit: count => (count < 2 ? "jour par diffusion" : "jours par diffusion"),
				caption: "Un jour, ou une série de jours consécutifs, payés en une fois.",
			},
		},
		steps: {
			heading: "Quatre étapes, et c’est en ligne.",
			lede:
				"Les quatre mêmes étapes que vous fait suivre la page de réservation. Calculer le prix d’une diffusion " +
				"ne demande aucun compte\u00a0; en réserver une demande une connexion, pour que vos réservations aient un " +
				"endroit où les retrouver.",
			stepLabel: position => `Étape ${position}`,
			steps: [
				{
					title: "Choisissez une apparence",
					line: "Arrière-plans et pleines pages, chacun avec un aperçu en direct pendant que vous choisissez.",
				},
				{
					title: "Ajoutez votre visuel",
					line: "PNG, JPEG ou WebP, plus un titre, un bouton et le lien qu’il ouvre.",
				},
				{
					title: "Choisissez vos dates",
					line: "Un jour ou une série de jours consécutifs. Un jour est libre ou il ne l’est pas.",
				},
				{
					title: "Payez une fois",
					line: "Le total est calculé par jour, TVA au moment du paiement. Une personne l’examine avant diffusion.",
				},
			],
		},
		treatments: {
			heading: treatments => (treatments < 2
				? "Une seule façon d’occuper le cadre."
				: `${treatments} façons d’occuper le cadre.`),
			lede:
				"Un arrière-plan s’anime derrière la carte de téléchargement\u00a0: la page continue de remplir son rôle " +
				"pendant que votre marque occupe le cadre. Une pleine page occupe tout l’écran, avec vos mots composés " +
				"dessus. Chaque aperçu ci-dessous utilise le vrai moteur de rendu.",
		},
		audience: {
			heading: "Qui est de l’autre côté",
			points: [
				{
					lead: "Des personnes qui attendent leurs fichiers.",
					rest:
						"Un destinataire n’a besoin ni de compte, ni de plan, ni d’application. Il a cliqué sur un " +
						"lien que lui a envoyé quelqu’un qu’il connaît.",
				},
				{
					lead: "Une attention qui n’est pas partagée.",
					rest: "Personne ne passe son chemin devant un téléchargement qu’il attend.",
				},
				{
					lead: "Large, pas ciblé.",
					rest:
						"Nous ne profilons pas les destinataires et ne vendons pas de segments. Vous achetez un jour " +
						"sur une page, pas une tranche de foule.",
				},
			],
		},
		promises: {
			heading: "Ce que nous promettons",
			points: [
				{lead: "Une personne examine chaque réservation", rest: "avant qu’elle n’atteigne un destinataire."},
				{
					lead: "Un remboursement intégral si nous ne pouvons pas la diffuser.",
					rest:
						"Nous préférons vous rendre votre argent plutôt que de mettre quelque chose devant un inconnu " +
						"qui ne l’a pas demandé.",
				},
				{
					lead: "Toujours signalée comme publicité,",
					rest:
						"sur chaque apparence et à chaque taille d’écran. Une obligation légale, jamais un choix de " +
						"mise en page.",
				},
			],
		},
		closer: {
			heading: "Réservez un jour en cinq minutes. Sans appel commercial.",
			paragraph:
				"Pas de formulaire de contact, pas de proposition, pas d’attente d’un rappel. Choisissez une " +
				"apparence, envoyez votre visuel, choisissez vos jours et payez. C’est tout.",
			availability: {
				aria: days => `Disponibilité pour les ${days} prochains jours`,
				title: days => `${days} prochains jours`,
				liveLabel: "Disponibilité en direct",
				nextFreeLabel: "Prochain jour libre\u00a0:",
				today: "aujourd’hui",
				tomorrow: "demain",
				onDate: (weekday, dayOfMonth, month) => `${weekday} ${dayOfMonth} ${month}`,
				noneFree: days => `Aucun jour libre dans les ${days} prochains jours`,
			},
		},
		cta: {
			label: "Lancer une publicité",
			note: "Pas besoin de compte pour consulter.",
		},
	},
	// Italian addresses the reader as Lei: impersonal verbs and infinitives on buttons, a capital on every pronoun.
	// A treatment is called stile and a run of booked days campagna throughout, so the hero, the steps and the terms strip agree.
	it: {
		seo: {
			title: "Pubblicità: download sponsorizzati",
			description:
				"Il Suo marchio sulla pagina che i destinatari guardano mentre arrivano i file. " +
				"Un solo inserzionista, prenotato a giornata e controllato da una persona.",
		},
		heading: "La pubblicità su Transferify",
		hero: {
			headline: "Conquisti il momento in cui tutti aspettano.",
			accentWord: "aspettano",
			lede:
				"Ogni file che consegniamo termina su una pagina di download che qualcuno sta guardando con " +
				"attenzione. Quella pagina si prenota a giornata. Nessun secondo inserzionista, nessuna rotazione, " +
				"nessuna quota di visibilità da negoziare.",
			secondary: "Vedere il calendario e la tariffa di oggi",
			mock: {
				frameAria:
					"La pagina di download che vede un destinatario, con uno sfondo pubblicitario attivo dietro la scheda di download.",
				url: host => `${host}/download/…`,
				sender: "Ana Le ha inviato",
				fileCount: "2 file",
				files: [
					{kind: "MP4", name: "Video_brand_finale.mp4", size: "1,2\u00a0GB"},
					{kind: "PDF", name: "Catalogo_2026.pdf", size: "48\u00a0MB"},
				],
				download: "Scaricare tutto",
				ad: {
					headline: "Il Suo titolo qui",
					sponsor: "Il Suo marchio",
					tagline: "Una seconda riga facoltativa.",
					cta: "Per saperne di più",
				},
			},
			demoCaption: (treatment, treatments) =>
				`Un esempio dal vivo: ${treatment}, uno degli stili disponibili (${treatments} in tutto), con il vero ` +
				"motore di rendering e grafiche segnaposto.",
		},
		terms: {
			aria: "Che cosa include una prenotazione",
			liveLabel: "In diretta",
			advertisers: {
				unit: () => "inserzionista al giorno",
				caption: "Un giorno è Suo oppure di qualcun altro. Nessuna rotazione, nessuna quota di visibilità.",
			},
			treatments: {
				unit: count => (count === 1 ? "stile" : "stili"),
				caption: "Ciascuno si anima in modo diverso, dietro la scheda di download o su tutta la pagina.",
			},
			runLength: {
				unit: count => (count === 1 ? "giorno per campagna" : "giorni per campagna"),
				caption: "Un giorno, oppure una serie di giorni consecutivi, pagati in un’unica soluzione.",
			},
		},
		steps: {
			heading: "Quattro passaggi e la campagna è online.",
			lede:
				"Sono gli stessi quattro passaggi della pagina di prenotazione. Calcolare il prezzo di una campagna " +
				"non richiede un account; per prenotarla serve accedere, così le prenotazioni restano raccolte in " +
				"un unico posto.",
			stepLabel: position => `Passaggio ${position}`,
			steps: [
				{
					title: "Scegliere uno stile",
					line: "Fondali e sfondi a tutta pagina, ciascuno con un’anteprima dal vivo durante la scelta.",
				},
				{
					title: "Aggiungere la grafica",
					line: "PNG, JPEG o WebP, più un titolo, un pulsante e il link che apre.",
				},
				{
					title: "Scegliere le date",
					line: "Un giorno o una serie di giorni consecutivi. Un giorno è libero oppure non lo è.",
				},
				{
					title: "Pagare una sola volta",
					line: "Il totale è calcolato per giorno, imposte al momento del pagamento. Una persona lo controlla prima della pubblicazione.",
				},
			],
		},
		treatments: {
			heading: treatments => (treatments === 1
				? "Un solo modo per occupare la scena."
				: `${treatments} modi per occupare la scena.`),
			lede:
				"Un fondale si anima dietro la scheda di download: la pagina continua a svolgere il proprio compito " +
				"mentre il marchio riempie la scena. Uno sfondo a tutta pagina occupa l’intera pagina, con il testo " +
				"dell’inserzione impaginato sopra. Ogni anteprima qui sotto usa il vero motore di rendering.",
		},
		audience: {
			heading: "Chi c’è dall’altra parte",
			points: [
				{
					lead: "Persone che aspettano i propri file.",
					rest:
						"Un destinatario non ha bisogno di un account, di un piano né di un’app. Ha cliccato su un " +
						"link inviato da qualcuno che conosce.",
				},
				{
					lead: "Un’attenzione non divisa.",
					rest: "Nessuno scorre oltre un download che sta aspettando.",
				},
				{
					lead: "Ampio, non mirato.",
					rest:
						"Non profiliamo i destinatari e non vendiamo segmenti. Si acquista una giornata su una " +
						"pagina, non una porzione di pubblico.",
				},
			],
		},
		promises: {
			heading: "Che cosa promettiamo",
			points: [
				{lead: "Una persona controlla ogni prenotazione", rest: "prima che raggiunga un destinatario."},
				{
					lead: "Rimborso completo se non possiamo pubblicarla.",
					rest:
						"Preferiamo restituire il denaro piuttosto che mettere qualcosa davanti a un estraneo che " +
						"non l’ha richiesto.",
				},
				{
					lead: "Sempre indicata come pubblicità,",
					rest:
						"su ogni stile e a ogni dimensione dello schermo. Un obbligo di legge, mai una scelta di " +
						"impaginazione.",
				},
			],
		},
		closer: {
			heading: "Prenotare una giornata in cinque minuti. Nessuna telefonata commerciale.",
			paragraph:
				"Nessun modulo di contatto, nessuna proposta, nessuna attesa di essere richiamati. Si sceglie uno " +
				"stile, si carica la grafica, si scelgono i giorni e si paga. Tutto qui.",
			availability: {
				aria: days => `Disponibilità per i prossimi ${days} giorni`,
				title: days => `Prossimi ${days} giorni`,
				liveLabel: "Disponibilità in tempo reale",
				nextFreeLabel: "Prossimo giorno libero:",
				today: "oggi",
				tomorrow: "domani",
				onDate: (weekday, dayOfMonth, month) => `${weekday} ${dayOfMonth} ${month}`,
				noneFree: days => `Nessun giorno libero nei prossimi ${days} giorni`,
			},
		},
		cta: {
			label: "Avviare una campagna pubblicitaria",
			note: "Per guardare non serve un account.",
		},
	},
});
